import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { TextField, MessageBar, MessageBarType, PrimaryButton, Label, Toggle, Icon } from 'office-ui-fabric-react';
import * as _yup from "yup";
import 'react-sortable-tree/style.css';
import moment from "moment"
import { v4 as uuidv4 } from "uuid";

import { RootState } from "app/redux/reducer";
import { setPageData, setPageErrors, initialPageData } from 'app/redux/page/actions';
import { toggleConfirmDialog } from 'app/redux/system/actions';
import { MetaTypeEnum, StatusEnum } from 'app/shared';
import { castThunkAction } from 'app/utils/casting';
import { toggleModal } from 'app/redux/modal/actions';
import { updateActivityType, deleteActivityType, getActivityType, getActivityTypeByLanguage, IActivityType, IActivityTypeRule } from 'app/services/activity-type';

import PageTitle from 'components/customs/PageTitle';
import Sidebar from 'components/customs/Sidebar/Sidebar';
import PageLayout from 'containers/PageLayout';
import { getConfiguration, IThemeSettings } from 'app/services/configuration';
import TranslatorPostBar from 'components/customs/TranslatorBar/TranslatorPostBar';
import MetaSingleFile from 'components/customs/MetaFields/Fields/MetaSingleFile';
import FormAccordion from 'components/customs/FormAccordion';
import MetaEditor from 'components/customs/MetaFields/Fields/MetaEditor';
import { ReactSortable } from 'react-sortablejs';

function mapStateToProps(state: RootState, ownProps: RouteComponentProps<{ activityTypeId: string }>) {
    return {
        ...ownProps,
        defaultLanguage: state.system.defaultLanguage,
        activityTypeId: ownProps.match.params.activityTypeId,
        language: state.system.language,
        pageData: state.page.pageData as IActivityType,
        pageErrors: state.page.pageErrors,
        contentTypes: state.contentType.contentTypes.list
    }
}

function mapDispatchToProps(dispatch: Dispatch) {
    return {
        ...bindActionCreators({
            setPageData,
            setPageErrors,
            initialPageData,
            updateActivityType,
            deleteActivityType,
            toggleConfirmDialog,
            getActivityType,
            getActivityTypeByLanguage,
            getConfiguration,
            toggleModal
        }, dispatch)
    };
}

type EditActivityTypeProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

interface OwnState {
    messageType: MessageBarType;
    configurationData: IThemeSettings;
    sortableMode: boolean;
    activeAccordion: number;
}

class EditActivityType extends React.Component<EditActivityTypeProps, OwnState> {

    state = {
        messageType: MessageBarType.warning,
        configurationData: {} as IThemeSettings,
        sortableMode: false,
        activeAccordion: -1
    }

    UNSAFE_componentWillMount() {
        castThunkAction<IThemeSettings>(this.props.getConfiguration(true)).then(configuration => {
            if (configuration) {
                this.setState({
                    configurationData: configuration
                })
            }
            this.props.getActivityType(this.props.activityTypeId);
        })
    }

    resetData() {
        this.setState({
            messageType: MessageBarType.warning
        }, () => {
            this.props.initialPageData<IActivityType>({
                key: "",
                title: "",
                description: "",
                content: "",
                rules: [],
                order: 30,
                isShowList: false,
                language: this.props.defaultLanguage,
                relationId: "",
            })
        })
    }

    renderTextByMessageType() {
        switch (this.state.messageType) {
            case MessageBarType.success:
                return "Kaydetme işlemi başarıyla gerçekleşmiştir."
            case MessageBarType.error:
                return "Kayıt işlemi yapılırken bir hata oluşmuştur. Lütfen servis sağlayıcınızla iletişime geçin."
            default:
                return "Aşağıdaki değerler üzerinden düzenleme yapabilirsiniz."
        }
    }

    renderMessageBar() {
        if (this.state.messageType !== MessageBarType.warning)
            return (
                <MessageBar
                    messageBarType={this.state.messageType}
                    isMultiline={false}
                >
                    {this.renderTextByMessageType()}
                </MessageBar>
            )
        return null;
    }

    onChangeLanguage(language: string) {
        if (language !== this.props.pageData.language) {
            this.props.getActivityTypeByLanguage(this.props.pageData, language);
        }
    }

    onCloneLanguage() {
        this.props.getActivityTypeByLanguage(this.props.pageData, this.props.pageData.language, true)
    }

    renderTranslatorBar() {
        if (!this.state.configurationData?.config?.languages || this.state.configurationData?.config?.languages.length <= 1) return null;

        return (
            <TranslatorPostBar
                languages={this.state.configurationData?.config?.languages?.map(item => item.code?.split("-")[0])}
                onChange={(language) => this.onChangeLanguage(language)}
                toggleDialog={this.props.toggleConfirmDialog}
                current={this.props.pageData.language}
                onClone={() => this.onCloneLanguage()}
                default={this.props.defaultLanguage}
            />
        )
    }

    onDelete() {
        this.props.toggleConfirmDialog(true, {
            callback: () => {
                let data = this.props.pageData;

                if (this.props.pageData.language !== this.props.defaultLanguage) {
                    castThunkAction<IActivityType>(this.props.getActivityType(this.props.activityTypeId as string, this.props.defaultLanguage, true)).then(response => {
                        data = response;

                        castThunkAction(this.props.deleteActivityType(this.props.pageData.id as string)).then(res => {
                            if (res) {
                                this.props.initialPageData(data)
                            }
                        });
                    });
                }
                else {
                    castThunkAction(this.props.deleteActivityType(this.props.pageData.id as string)).then(res => {
                        if (res) {
                            this.props.history.push("/activity-types")
                        }
                    })
                }

            }
        })
    }

    /**
     * Validation Şemasını Kontrol Ederek, Eğer Validate ise Doğrulama modalını aktive eder, Değilse İlgili Yerlere Hata Dönecek Yani,
     * formErrors Objesini Dolduracak Action'u Tetikler.
     */
    onSubmit() {
        const {
            pageData,
            setPageErrors
        } = this.props;
        const validationSchema = _yup.object({
            title: _yup.string().required(
                "Bu alan zorunludur!"
            )
        });
        let formErrors = {};
        validationSchema
            .validate(pageData, {
                abortEarly: false
            })
            .then(() => {
                setPageErrors({});
                castThunkAction(this.props.updateActivityType(this.props.pageData)).then(res => {
                    window.scrollTo({ top: 0 });
                    this.setState({
                        messageType: res ? MessageBarType.success : MessageBarType.error
                    })
                })
            })
            .catch(err => {
                err.inner.forEach((e: any) => {
                    const { path, message } = e;
                    formErrors[path] = message;
                });
                setPageErrors(formErrors);
            });
    };

    renderForm() {
        return (
            <div className="form-groups row">
                <div className="form-group col-md-12">
                    <FormAccordion showContent={true} title="Genel Bilgiler">
                        <div className="form-wrapper row" style={{ padding: "10px 5px" }}>
                            <div className="item row col-md-6">
                                <TextField
                                    className="custom-textfield"
                                    label="Etkinlik Türü Adı"
                                    value={this.props.pageData.title}
                                    onChange={(event: any) => {
                                        this.props.setPageData("title", event.target.value)
                                    }}
                                />
                            </div>
                            <div className="item row col-md-6">
                                <TextField
                                    className="custom-textfield"
                                    label="Etkinlik Türü Anahtarı"
                                    value={this.props.pageData.key}
                                    onChange={(event: any) => {
                                        this.props.setPageData("key", event.target.value)
                                    }}
                                />
                            </div>
                            <div className="item row col-md-12">
                                <TextField
                                    className="custom-textfield"
                                    label="Etkinlik Türü Açıklaması"
                                    value={this.props.pageData.description}
                                    onChange={(event: any) => {
                                        this.props.setPageData("description", event.target.value)
                                    }}
                                    multiline={true}
                                    resizable={false}
                                />
                            </div>
                            <div className="item row col-md-12">
                                <Label>Etkinlik Türü İçeriği</Label>
                                <MetaEditor
                                    type={MetaTypeEnum.Editor}
                                    onChange={(value: string) => {
                                        this.props.setPageData("content", value)
                                    }}
                                    meta={{ data: this.props.pageData.content, type: MetaTypeEnum.Editor, title: '', column: 12, name: '' }}
                                    toggleModal={toggleModal}
                                />
                            </div>
                            <div className="item row col-md-12" style={{ paddingTop: 8 }}>
                                <Toggle
                                    className="custom-toggle m-left-auto"
                                    onChange={(_, checked) => this.props.setPageData("isShowList", checked)}
                                    checked={this.props.pageData.isShowList}
                                    label="Etkinlik Türü Altında Etkinlik Olmasa Dahi Listelerde Gözüksün Mü?"
                                />
                            </div>
                        </div>
                    </FormAccordion>
                </div>
            </div>
        )
    }

    renderRuleItems() {
        return (
            (this.props.pageData.rules || []).map((rule: IActivityTypeRule, index: number) => (
                <div
                    key={index}
                    className={
                        this.state.activeAccordion ===
                            index
                            ? "fa-Accordion-item active"
                            : "fa-Accordion-item"
                    }
                >
                    <div className="title row">
                        <div
                            onClick={() => {
                                this.setState({ activeAccordion: this.state.activeAccordion !== index ? index : -1 })
                            }}
                            className="text col no-padding"
                        >
                            {rule.title || `Kural ${index + 1}`}
                        </div>
                        <Icon
                            onClick={() => {
                                const rules = [...this.props.pageData.rules]

                                rules.splice(index, 1);
                                this.props.setPageData("rules", rules);
                            }}
                            className="m-left-auto remove-accordion"
                            iconName="ChromeClose"
                        />
                    </div>
                    {this.state.activeAccordion === index && (
                        <div className="content">
                            <div className="item row col-md-12">
                                <TextField
                                    className="custom-textfield"
                                    label="Başlık"
                                    value={rule.title}
                                    onChange={(event: any) => {
                                        const rules = [...this.props.pageData.rules]

                                        rules[index].title = event.target.value
                                        this.props.setPageData("rules", rules);
                                    }}
                                />
                            </div>
                            <div className="item row col-md-12">
                                <Label>İçerik</Label>
                                <MetaEditor
                                    type={MetaTypeEnum.Editor}
                                    onChange={(value: string) => {
                                        const rules = [...this.props.pageData.rules]

                                        rules[index].content = value
                                        this.props.setPageData("rules", rules);
                                    }}
                                    meta={{ data: rule.content, type: MetaTypeEnum.Editor, title: '', column: 12, name: '' }}
                                    toggleModal={toggleModal}
                                />
                            </div>
                        </div>
                    )}
                </div>
            ))
        )
    }

    renderRules() {
        return (
            <div className="form-groups row">
                <div className="form-group col-md-12" style={{ paddingTop: 0 }}>
                    <FormAccordion showContent={true} title="Etkinlik Kural Bilgileri">
                        <div className="form-wrapper row" style={{ padding: "15px 20px" }}>
                            <div className="accordion-wrapper-header">
                                <div className="accordion-wrapper-header-title">Kurallar</div>
                                <PrimaryButton
                                    iconProps={{ iconName: "CalculatorAddition" }}
                                    className="custom-button"
                                    text="Yeni Ekle"
                                    onClick={() => {
                                        this.props.setPageData("rules", [...this.props.pageData.rules, { id: uuidv4(), title: '', content: '' }]);
                                    }}
                                    styles={{
                                        root: {
                                            fontSize: 11,
                                            background: "#33926e !important",
                                            borderColor: "#33926e !important"
                                        }
                                    }}
                                />
                                <Toggle
                                    className="custom-toggle m-left-auto"
                                    onChange={(_, checked) => this.setState({ sortableMode: checked as boolean })}
                                    checked={this.state.sortableMode}
                                    label="Sıralama Modu"
                                    inlineLabel
                                    styles={{
                                        root: {
                                            marginBottom: "-5px",
                                            marginTop: "-1px !important"
                                        },
                                        container: {
                                            marginTop: "-1px !important"
                                        }
                                    }}
                                />
                            </div>
                            <div className="fa-Accordion accordion-wrapper-content">
                                {this.state.sortableMode ? (
                                    <ReactSortable
                                        list={this.props.pageData.rules || []}
                                        setList={(rules: IActivityTypeRule[]) => this.props.setPageData("rules", rules)}
                                        animation={150}
                                    >
                                        {this.renderRuleItems()}
                                    </ReactSortable>
                                ) : this.renderRuleItems()}
                            </div>
                        </div>
                    </FormAccordion>
                </div>
            </div>
        )
    }

    renderImages() {
        return (
            <div className="form-groups row">
                <div className="form-group col-md-12" style={{ paddingTop: 0 }}>
                    <FormAccordion showContent={true} title="Görsel Ayarları">
                        <div className="form-wrapper row" style={{ padding: "10px 5px" }}>
                            <div className="item row col-md-6">
                                <Label>Yatay Görsel</Label>
                                <MetaSingleFile
                                    type={MetaTypeEnum.SingleImage}
                                    onChange={(value) => {
                                        this.props.setPageData("profileImageHorizontal", value)
                                    }}
                                    meta={{ data: this.props.pageData.profileImageHorizontal, type: MetaTypeEnum.SingleImage, title: '', column: 12, name: '' }}
                                    toggleModal={toggleModal}
                                />
                            </div>
                            <div className="item row col-md-6">
                                <Label>Dikey Görsel</Label>
                                <MetaSingleFile
                                    type={MetaTypeEnum.SingleImage}
                                    onChange={(value) => {
                                        this.props.setPageData("profileImageVertical", value)
                                    }}
                                    meta={{ data: this.props.pageData.profileImageVertical, type: MetaTypeEnum.SingleImage, title: '', column: 12, name: '' }}
                                    toggleModal={toggleModal}
                                />
                            </div>
                        </div>
                    </FormAccordion>
                </div>
            </div>
        )
    }
    render() {
        const { pageData } = this.props;
        return (
            <PageLayout history={this.props.history} match={this.props.match} location={this.props.location}>
                <div className="page push-all">
                    <PageTitle
                        title="Düzenle"
                        titleRenderer={
                            () => (
                                <span className="edit-title">
                                    Düzenle:
                                    <strong>{this.props.pageData?.title || "-"}</strong>
                                </span>
                            )
                        }
                        showBack
                        backUrl="/activity-types"
                        history={this.props.history}
                        subTitle="Aşağıdan geçerli etkinlik türünü düzenleyebilirsiniz."
                    />
                    <PrimaryButton
                        onClick={() => this.props.history.push('/activity-type/create')}
                        iconProps={{ iconName: "Add" }}
                        className="absolute-create"
                        text="Yeni Etkinlik Türü Ekle"
                        styles={{
                            root: {
                                background: "#62a98e",
                                borderColor: "#5b9e85",
                                color: "#fff"
                            },
                            rootHovered: {
                                background: "#5b9e85",
                                borderColor: "#5b9e85"
                            },
                            rootDisabled: {
                                background: "#ececec"
                            }
                        }}
                    />
                    {this.renderTranslatorBar()}
                    <div className="content row">
                        <div className="left col no-padding">
                            {this.renderMessageBar()}
                            {this.renderForm()}
                            {this.renderRules()}
                            {this.renderImages()}
                        </div>
                        <Sidebar
                            publishBar={{
                                onSave: () => this.onSubmit(),
                                onDelete: () => this.onDelete(),
                                hidePreviewButton: true,
                                hideDraftButton: true,
                                isEdit: true,
                                status: StatusEnum.Published,
                                userLabel: pageData.updatedBy ? 'Ekleyen Kullanıcı:' : 'Düzenleyen Kullanıcı:',
                                publishDateText: pageData.updatedBy ? 'Yayınlanma Tarihi:' : 'Eklenme Tarihi:',
                                dateText: moment((pageData.updatedAt ? pageData.updatedAt : pageData.createdAt)).format('DD.MM.YYYY, HH:mm'),
                                showOrder: true,
                                order: this.props.pageData?.order || 0,
                                onChangeOrderIndex: (order) => this.props.setPageData("order", order),
                            }}
                            categoryBar={{
                                hide: true
                            }}
                            archiveBar={{
                                hide: true
                            }}

                            layoutBar={{
                                hide: true
                            }}
                            thumbnailBar={{
                                hide: true
                            }}
                        />
                    </div>
                </div>
            </PageLayout>
        )
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(EditActivityType);